import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCompose } from '../lib/compose';
import { PostCard } from '../components/PostCard';
import { formatMastheadDate } from '../lib/format';
import { dividerLabel, editionStart, olderLabel, splitByEdition } from '../lib/edition';

// editionLabel — rotates through the day so the paper feels like a live
// broadcast. Uses local browser time (SPA renders once per session; if
// they leave the tab open past midnight the label doesn't update, that's
// fine).
function editionLabel(): string {
    const h = new Date().getHours();
    if (h >= 5  && h < 9)  return 'Early Edition';
    if (h >= 9  && h < 12) return 'Morning Edition';
    if (h >= 12 && h < 15) return 'Midday';
    if (h >= 15 && h < 19) return 'Late Edition';
    return 'Evening Edition';
}

// groupPostsByDay partitions posts into SAST day-buckets, newest first.
// The top bucket is "Today" (>= SAST midnight); each earlier day gets its
// own mini-paper stacked below, separated by a full-width divider so you
// can scroll day by day. Anchored to SAST via editionStart('daily') so a
// reader in London doesn't see 22:00 SAST posts slide under "yesterday".
function groupPostsByDay(posts: Post[]): Array<{ key: string; label: string; posts: Post[] }> {
    const todayStart = editionStart('daily').getTime();
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const byKey: Record<string, { key: string; label: string; posts: Post[]; sortTs: number }> = {};
    for (const p of posts) {
        const iso = p.topic_day ?? p.created_at;
        const ts  = new Date(iso).getTime();
        // Snap to the SAST day boundary the post belongs to. Days are 24h
        // apart from todayStart, so integer-divide the delta.
        const dayIdx = Math.floor((todayStart - ts) / ONE_DAY_MS);
        const dayBoundary = todayStart - dayIdx * ONE_DAY_MS;
        const key = new Date(dayBoundary).toISOString().slice(0, 10);
        let label: string;
        if (dayIdx <= 0)     label = 'Today';
        else if (dayIdx === 1) label = 'Yesterday';
        else                   label = new Date(dayBoundary).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
        if (!byKey[key]) byKey[key] = { key, label, posts: [], sortTs: dayBoundary };
        byKey[key].posts.push(p);
    }
    return Object.values(byKey).sort((a, b) => b.sortTs - a.sortTs);
}

// dailyPaperSections applies the newspaper's kind-based section layout
// to one day's posts. Shared between today's edition and each older
// day so the reader sees a familiar structure scrolling down.
function dailyPaperSections(dayPosts: Post[]): Array<[string, Post[]]> {
    const known = new Set(['offer', 'event', 'notice']);
    return [
        ['Tonight',           dayPosts.filter(p => p.post_type === 'offer')],
        ['This Week',         dayPosts.filter(p => p.post_type === 'event')],
        ['Notices',           dayPosts.filter(p => p.post_type === 'notice')],
        ['Around the Suburb', dayPosts.filter(p => !known.has(p.post_type))],
    ].filter(([, list]) => list.length > 0) as Array<[string, Post[]]>;
}
import type { Group, PinnedCommunity, Post } from '../types/api';

interface ListResp { posts: Post[] }
interface WeatherSnapshot {
    temp_c: number;
    feels_like_c: number;
    wind_kmh: number;
    wind_dir: string;         // compass abbrev for a11y fallback
    wind_dir_deg?: number;    // raw bearing for SVG arrow rotation
    humidity_pct?: number;
    cloud_pct: number;
    precip_mm: number;
    symbol: string;
    blurb: string;
    blurb_long: string;
    valid_until: string;
    refreshed_at: string;
    source: string;
}
interface PaperResp {
    masthead: string;
    posts: Post[];
    scores: Record<string, unknown>;
    weather?: WeatherSnapshot;
}

export function Paper() {
    const api = useApi();
    const { authed, login } = useAuth();
    const { openCompose } = useCompose();
    const [posts, setPosts] = useState<Post[]>([]);
    const [masthead, setMasthead] = useState<string>('Your Paper');
    const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
    const [groupSections,     setGroupSections]     = useState<Array<{ group: Group; posts: Post[] }>>([]);
    const [communitySections, setCommunitySections] = useState<Array<{ pinned: PinnedCommunity; posts: Post[] }>>([]);
    const [loadState, setLoadState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
    const [loadErr,   setLoadErr]   = useState<string>('');

    const load = useCallback(async () => {
        setLoadState('loading'); setLoadErr('');
        try {
            if (!authed) {
                // Anon reader — hit the public paper. Renders the default
                // community's front page (staff picks which via /admin) with
                // no personalization + no group/community sections. Everything
                // is a signup lure from here.
                const r = await api.get<PaperResp>('/api/public/paper?limit=50');
                setPosts(r.posts ?? []);
                setMasthead(r.masthead || 'The Paper');
                setWeather(r.weather ?? null);
                setGroupSections([]);
                setCommunitySections([]);
                setLoadState('done');
                return;
            }
            // Personalized paper — merged, scored, ranked by /api/paper.
            // Replaces the old hardcoded /api/tags/vredehoek query. The
            // masthead comes back derived from the reader's home community.
            const r = await api.get<PaperResp>('/api/paper?limit=50');
            setPosts(r.posts ?? []);
            setMasthead(r.masthead || 'Your Paper');
            setWeather(r.weather ?? null);

            // In parallel: pull my groups + pinned communities, then fetch a
            // few posts per section. Empty sections don't render.
            const [gRes, pRes] = await Promise.all([
                api.get<{ groups: Group[]                       }>('/api/groups?mine=1').catch(() => ({ groups: [] })),
                api.get<{ pinned_communities: PinnedCommunity[] }>('/api/me/pinned-communities').catch(() => ({ pinned_communities: [] })),
            ]);
            const groups = gRes.groups ?? [];
            const pinned = pRes.pinned_communities ?? [];

            const groupBuckets = await Promise.all(groups.map(async g => {
                try {
                    const gp = await api.get<ListResp>('/api/posts?posted_as_group_id=' + g.id + '&limit=5');
                    return { group: g, posts: gp.posts ?? [] };
                } catch { return { group: g, posts: [] as Post[] }; }
            }));
            const commBuckets = await Promise.all(pinned.map(async p => {
                try {
                    const cp = await api.get<ListResp>('/api/posts?community_id=' + p.community_id + '&limit=5');
                    return { pinned: p, posts: cp.posts ?? [] };
                } catch { return { pinned: p, posts: [] as Post[] }; }
            }));
            setGroupSections(groupBuckets.filter(b => b.posts.length > 0));
            setCommunitySections(commBuckets.filter(b => b.posts.length > 0));

            setLoadState('done');
        } catch (e) {
            console.error('paper load failed', e);
            setLoadErr((e as Error).message);
            setLoadState('error');
        }
    }, [api, authed]);

    useEffect(() => { load(); }, [load]);

    // Refresh when the compose modal reports a successful publish.
    useEffect(() => {
        function onCreated() { load(); }
        window.addEventListener('onhere:post-created', onCreated);
        return () => window.removeEventListener('onhere:post-created', onCreated);
    }, [load]);

    // Also open the compose modal if we arrived via ?compose=1 (some deep-links
    // land here — legacy pages' "+" button navigated to /?compose=1).
    useEffect(() => {
        if (authed && new URLSearchParams(location.search).get('compose') === '1') {
            openCompose();
        }
    }, [authed, openCompose]);

    // Anon paper renders below with a persistent lure banner across the top.
    // Empty-paper edge case for anon (no public community configured, or
    // configured community has no posts) → friendly pitch + sign-in CTA
    // instead of the "hit the + up top" line that assumes an authed user.
    if (!authed && loadState === 'done' && posts.length === 0) return (
        <div id="prelogin" className="prelogin">
            <h1>Your suburb's noticeboard.</h1>
            <p>Nothing on the paper today. Sign up to be the first.</p>
            <button type="button" className="plain" onClick={login}>Sign in</button>
        </div>
    );

    // Lead article = newest post with an image, regardless of kind. Real
    // newspapers pick the lead by the strongest visual + newest news, not by
    // any single category. Fall back to the newest post overall when nothing
    // has an image so the front page never looks empty.
    const lead = posts.find(p => !!p.media_url) || posts[0];
    const rest = lead ? posts.filter(p => p !== lead) : posts;

    // Group by day first. Today's edition renders with the full paper
    // layout (lead + kind-based sections); older days stack below with
    // full-width dividers so you can scroll down day by day.
    const dayGroups = groupPostsByDay(rest);
    const todayGroup = dayGroups.find(g => g.label === 'Today');
    const olderGroups = dayGroups.filter(g => g.label !== 'Today');
    const sections = dailyPaperSections(todayGroup?.posts ?? []);

    return (
        <section id="view-paper">
            {!authed && (
                <div className="anon-lure">
                    You're reading as a guest.{' '}
                    <button type="button" onClick={login}>Sign in</button>{' '}
                    to react, comment, and post.
                </div>
            )}
            <div className="paper-wrap">
                <div className="nameplate-strip">
                    <span>Cape Town · {editionLabel()}</span>
                    <span>Vol. 1 · No. 1</span>
                    <span>Est. 2026 · Free</span>
                </div>
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>{masthead}</h1>
                    <div className="issue">{formatMastheadDate()} · Vol. 1 No. 1</div>
                    <div className="rule-bottom" />
                </div>
                {weather && <WeatherBanner w={weather} />}

                {loadState === 'loading' && (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px 24px', fontFamily: "'Newsreader',serif", fontStyle: 'italic' }}>
                        Loading posts…
                    </p>
                )}
                {loadState === 'error' && (
                    <p style={{ textAlign: 'center', color: '#8b1919', padding: '40px 24px', fontFamily: "'Newsreader',serif" }}>
                        Couldn't load posts: {loadErr}
                    </p>
                )}
                {loadState === 'done' && posts.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px 24px', fontFamily: "'Newsreader',serif", fontStyle: 'italic' }}>
                        No posts on the paper yet. Hit the "+" up top to publish one.
                    </p>
                )}

                {lead && <LeadArticle post={lead} />}

                <div className="cols">
                    {sections.map(([title, list]) => (
                        <section key={title}>
                            <h2>{title}</h2>
                            {list.map(p => <PostCard key={p.id} post={p} />)}
                        </section>
                    ))}
                </div>

                {olderGroups.map(g => {
                    const olderSecs = dailyPaperSections(g.posts);
                    if (olderSecs.length === 0) return null;
                    return (
                        <div key={g.key}>
                            <div className="day-divider day-divider-wide">
                                <span>{g.label}</span>
                            </div>
                            <div className="cols cols-shelf">
                                {olderSecs.map(([title, list]) => (
                                    <section key={g.key + '-' + title}>
                                        <h2>{title}</h2>
                                        {list.map(p => <PostCard key={p.id} post={p} />)}
                                    </section>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Per-community + per-group sections. Main paper is page 1, so
                    the first follow-section is page 2. Community sections come
                    before group sections in the count. Each community draws its
                    own cadence divider so a weekly community shows "This Week"
                    even when the main paper above is daily. */}
                {communitySections.map(({ pinned, posts: cposts }, idx) => {
                    const cadence = pinned.cadence ?? 'daily';
                    const { current, older } = splitByEdition(
                        cposts,
                        p => p.topic_day ?? p.created_at,
                        cadence,
                    );
                    return (
                        <section key={'c-' + pinned.community_id} className="follow-section">
                            <PageHeader n={idx + 2} masthead={masthead} />
                            <h2>
                                <Link to={'/community/' + pinned.slug} className="post-title-link">
                                    {dividerLabel(cadence)} in {pinned.name} →
                                </Link>
                            </h2>
                            {current.map(p => <PostCard key={p.id} post={p} />)}
                            {older.length > 0 && (
                                <>
                                    <div className="day-divider">
                                        <span>{olderLabel(cadence)}</span>
                                    </div>
                                    {older.map(p => <PostCard key={p.id} post={p} />)}
                                </>
                            )}
                        </section>
                    );
                })}
                {groupSections.map(({ group, posts: gposts }, idx) => (
                    <section key={'g-' + group.id} className="follow-section">
                        <PageHeader n={communitySections.length + idx + 2} masthead={masthead} />
                        <h2>From {group.name}</h2>
                        {gposts.map(p => <PostCard key={p.id} post={p} />)}
                    </section>
                ))}

                <div className="colophon">{masthead} · Est. 2026 · onhere.cc</div>
            </div>
        </section>
    );
}

function PageHeader({ n, masthead }: { n: number; masthead: string }) {
    return (
        <div className="page-header">
            <span className="page-num">Page {n}</span>
            <span className="page-title">{masthead}</span>
        </div>
    );
}

function LeadArticle({ post }: { post: Post }) {
    return (
        <article className="lead-article">
            {post.kicker && <div className="kicker">{post.kicker}</div>}
            <h2>
                <Link to={'/posts/' + post.id} className="post-title-link">{post.title || '(untitled)'}</Link>
            </h2>
            {post.subhead && <p className="subhead">{post.subhead}</p>}
            <div className="byline">
                By {post.display_identity}
                {post.topic && <> · <span className="topic-flair">{post.topic.replace(/_/g, ' ')}</span></>}
            </div>
            {post.media_url && <img className="lead-image" src={post.media_url} alt="" />}
            <div className="lead-body">
                <p>{post.body}</p>
            </div>
        </article>
    );
}

// WeatherBanner — thin strip flush against the masthead's bottom rule.
// Collapsed: yr.no icon + temp + feels-like + wind arrow + blurb.
// Click to expand: bigger visual card with humidity/cloud chips + long
// blurb + fresh timestamp + MET Norway attribution (their T&Cs require it).
function WeatherBanner({ w }: { w: WeatherSnapshot }) {
    const [open, setOpen] = useState(false);
    const bearing = typeof w.wind_dir_deg === 'number' ? w.wind_dir_deg : compassToBearing(w.wind_dir);
    return (
        <div className="weather-banner" onClick={() => setOpen(v => !v)} role="button" tabIndex={0}
             aria-expanded={open}
             onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); } }}>
            <div className="weather-line">
                <img src={`/weather-icons/${weatherIconFile(w.symbol)}.svg`}
                     alt={w.symbol.replace(/_/g, ' ')}
                     className="weather-icon" width={28} height={28} />
                <span className="weather-temp">{Math.round(w.temp_c)}°</span>
                <span className="weather-feels">≈ {Math.round(w.feels_like_c)}°</span>
                <WindArrow bearing={bearing} kmh={w.wind_kmh} />
                <span className="weather-blurb">{w.blurb}</span>
                <span className="weather-toggle" aria-hidden>{open ? '▴' : '▾'}</span>
            </div>
            {open && (
                <div className="weather-more">
                    <div className="weather-more-head">
                        <img src={`/weather-icons/${weatherIconFile(w.symbol)}.svg`}
                             alt="" className="weather-icon-lg" width={72} height={72} />
                        <div className="weather-more-nums">
                            <div className="weather-temp-lg">{Math.round(w.temp_c)}°</div>
                            <div className="weather-feels-lg">feels like {Math.round(w.feels_like_c)}°</div>
                            <div className="weather-chips">
                                {typeof w.humidity_pct === 'number' && <Chip label="humidity" value={`${Math.round(w.humidity_pct)}%`} />}
                                <Chip label="cloud" value={`${Math.round(w.cloud_pct)}%`} />
                                {w.precip_mm > 0 && <Chip label="rain 1h" value={`${w.precip_mm.toFixed(1)}mm`} />}
                                <Chip label={w.wind_dir} value={`${Math.round(w.wind_kmh)} km/h`} icon={<WindArrow bearing={bearing} kmh={w.wind_kmh} size={20} />} />
                            </div>
                        </div>
                    </div>
                    <p className="weather-blurb-long">{w.blurb_long}</p>
                    <p className="weather-attribution">
                        Weather data from MET Norway · updated {timeAgo(w.refreshed_at)}
                    </p>
                </div>
            )}
        </div>
    );
}

// WindArrow — SVG arrow rotated to the exact bearing. Bearing is
// meteorological "wind FROM direction", so the arrow shows where the
// wind is COMING FROM (points away from that direction).
function WindArrow({ bearing, kmh, size = 22 }: { bearing: number; kmh: number; size?: number }) {
    // Colour scales with speed: light for calm, dark for windy.
    const opacity = Math.min(1, 0.35 + kmh / 40);
    return (
        <svg width={size} height={size} viewBox="0 0 24 24"
             style={{ transform: `rotate(${bearing + 180}deg)`, verticalAlign: 'middle' }}
             aria-hidden>
            <path d="M12 3 L12 21 M12 3 L7 8 M12 3 L17 8"
                  stroke="#8b1919" strokeWidth={2.4} strokeLinecap="round"
                  strokeLinejoin="round" fill="none" opacity={opacity} />
        </svg>
    );
}

function Chip({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
    return (
        <span className="weather-chip">
            {icon}
            <span className="weather-chip-value">{value}</span>
            <span className="weather-chip-label">{label}</span>
        </span>
    );
}

// yr.no symbol codes vary — clearsky_day, partlycloudy_night, rain, etc.
// We ship the whole set in public/weather-icons/, so just pass through.
// Fallback to 'cloudy' if the model returns something we don't ship.
function weatherIconFile(symbol: string): string {
    if (!symbol) return 'cloudy';
    // Symbols may include a _day/_night/_polartwilight suffix. All are
    // in the icon set as-is.
    return symbol;
}

// compassToBearing — rough fallback when snapshot lacks wind_dir_deg
// (older snapshots from before we added the field). 16-point compass
// → degrees.
function compassToBearing(compass: string): number {
    const map: Record<string, number> = {
        N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
        E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
        S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
        W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
    };
    return map[compass] ?? 0;
}

// Local minimal "N minutes ago" — avoids importing yet another helper.
function timeAgo(iso: string): string {
    const then = new Date(iso).getTime();
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}
